/**
 * useWizardState Hook
 * Manages state for the LLM Generator Wizard Mode
 */
import { useState, useCallback, useMemo } from 'react';

// Wizard steps
export const WIZARD_STEPS = {
  UPLOAD: 1,
  PREVIEW: 2,
  CHUNKING: 3,
  GENERATE: 4
};

// Chunking strategies
export const CHUNKING_STRATEGIES = {
  COMPLETE: 'complete',
  PER_PAGE: 'per-page',
  BLOCKS: 'blocks'
};

/**
 * Initial wizard state
 */
const initialState = {
  currentStep: WIZARD_STEPS.UPLOAD,
  files: [], // Parsed files with pages
  chunkingStrategy: CHUNKING_STRATEGIES.COMPLETE,
  blockSize: 5,
  chunks: [], // Generated chunks for processing
  results: [], // Results per chunk
  currentChunkIndex: 0,
  isGenerating: false,
  generationError: null,
  prompt: 'Erstelle aus diesem Text Multiple-Choice-Fragen im JSON-Format. Jede Frage sollte eine klare Fragestellung und 4 Antwortmöglichkeiten haben, wobei genau eine richtig ist.'
};

/**
 * Create chunks based on strategy
 */
function createChunks(files, strategy, blockSize) {
  // Get all selected pages from all files
  const selectedPages = [];
  
  files.forEach(file => {
    file.pages.forEach(page => {
      if (page.selected) {
        selectedPages.push({
          ...page,
          fileName: file.name,
          fileId: file.id
        });
      }
    });
  });
  
  if (selectedPages.length === 0) {
    return [];
  }
  
  switch (strategy) {
    case CHUNKING_STRATEGIES.COMPLETE:
      return [{
        id: 'chunk-all',
        label: 'Alle Seiten',
        pages: selectedPages,
        text: selectedPages.map(p => p.text).join('\n\n---\n\n'),
        status: 'pending'
      }];
    
    case CHUNKING_STRATEGIES.PER_PAGE:
      return selectedPages.map((page, index) => ({
        id: `chunk-page-${index}`,
        label: `${page.fileName} - Seite ${page.pageNumber}`,
        pages: [page],
        text: page.text,
        status: 'pending'
      }));
    
    case CHUNKING_STRATEGIES.BLOCKS:
      const chunks = [];
      for (let i = 0; i < selectedPages.length; i += blockSize) {
        const block = selectedPages.slice(i, i + blockSize);
        const startPage = block[0].pageNumber;
        const endPage = block[block.length - 1].pageNumber;
        
        chunks.push({
          id: `chunk-block-${chunks.length}`,
          label: block.length === 1 
            ? `Seite ${startPage}` 
            : `Seiten ${startPage}-${endPage}`,
          pages: block,
          text: block.map(p => p.text).join('\n\n---\n\n'),
          status: 'pending'
        });
      }
      return chunks;
    
    default:
      return [];
  }
}

/**
 * Main wizard state hook
 */
export function useWizardState() {
  const [state, setState] = useState(initialState);
  
  // Navigation
  const goToStep = useCallback((step) => {
    setState(prev => ({ ...prev, currentStep: step }));
  }, []);
  
  const nextStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, WIZARD_STEPS.GENERATE)
    }));
  }, []);
  
  const prevStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, WIZARD_STEPS.UPLOAD)
    }));
  }, []);
  
  // File management
  const setFiles = useCallback((files) => {
    setState(prev => ({ ...prev, files }));
  }, []);
  
  const addFile = useCallback((newFile) => {
    setState(prev => ({
      ...prev,
      files: [...prev.files, newFile]
    }));
  }, []);
  
  const addFiles = useCallback((newFiles) => {
    setState(prev => ({
      ...prev,
      files: [...prev.files, ...newFiles]
    }));
  }, []);
  
  const removeFile = useCallback((fileId) => {
    setState(prev => ({
      ...prev,
      files: prev.files.filter(f => f.id !== fileId)
    }));
  }, []);
  
  // Page selection
  const togglePageSelection = useCallback((fileId, pageNumber) => {
    setState(prev => ({
      ...prev,
      files: prev.files.map(file => {
        if (file.id !== fileId) return file;
        return {
          ...file,
          pages: file.pages.map(page => {
            if (page.pageNumber !== pageNumber) return page;
            return { ...page, selected: !page.selected };
          })
        };
      })
    }));
  }, []);
  
  const selectAllPages = useCallback((fileId, selected = true) => {
    setState(prev => ({
      ...prev,
      files: prev.files.map(file => {
        if (fileId && file.id !== fileId) return file;
        return {
          ...file,
          pages: file.pages.map(page => ({ ...page, selected }))
        };
      })
    }));
  }, []);
  
  const selectPageRange = useCallback((fileId, startPage, endPage) => {
    setState(prev => ({
      ...prev,
      files: prev.files.map(file => {
        if (file.id !== fileId) return file;
        return {
          ...file,
          pages: file.pages.map(page => ({
            ...page,
            selected: page.pageNumber >= startPage && page.pageNumber <= endPage
          }))
        };
      })
    }));
  }, []);
  
  // Page text editing
  const updatePageText = useCallback((fileId, pageNumber, newText) => {
    setState(prev => ({
      ...prev,
      files: prev.files.map(file => {
        if (file.id !== fileId) return file;
        return {
          ...file,
          pages: file.pages.map(page => {
            if (page.pageNumber !== pageNumber) return page;
            return {
              ...page,
              text: newText,
              edited: newText !== page.originalText
            };
          })
        };
      })
    }));
  }, []);
  
  // Chunking
  const setChunkingStrategy = useCallback((strategy) => {
    setState(prev => ({ ...prev, chunkingStrategy: strategy }));
  }, []);
  
  const setBlockSize = useCallback((size) => {
    setState(prev => ({ ...prev, blockSize: size }));
  }, []);
  
  const generateChunks = useCallback(() => {
    setState(prev => {
      const chunks = createChunks(prev.files, prev.chunkingStrategy, prev.blockSize);
      return { ...prev, chunks };
    });
  }, []);
  
  // Generation
  const startGeneration = useCallback(() => {
    setState(prev => ({
      ...prev,
      isGenerating: true,
      currentChunkIndex: 0,
      results: [],
      generationError: null
    }));
  }, []);
  
  const completeChunk = useCallback((index, success, questions = null, error = null) => {
    setState(prev => ({
      ...prev,
      results: [...prev.results.slice(0, index), { success, questions, error }, ...prev.results.slice(index + 1)],
      currentChunkIndex: index + 1
    }));
  }, []);
  
  const setIsGenerating = useCallback((isGenerating) => {
    setState(prev => ({ ...prev, isGenerating }));
  }, []);
  
  const setPrompt = useCallback((prompt) => {
    setState(prev => ({ ...prev, prompt }));
  }, []);
  
  const failChunk = useCallback((chunkId, error) => {
    setState(prev => ({
      ...prev,
      chunks: prev.chunks.map(chunk => 
        chunk.id === chunkId 
          ? { ...chunk, status: 'failed', error }
          : chunk
      ),
      currentChunkIndex: prev.currentChunkIndex + 1
    }));
  }, []);
  
  const finishGeneration = useCallback(() => {
    setState(prev => ({ ...prev, isGenerating: false }));
  }, []);
  
  const setGenerationError = useCallback((error) => {
    setState(prev => ({
      ...prev,
      generationError: error,
      isGenerating: false
    }));
  }, []);
  
  // Reset
  const reset = useCallback(() => {
    setState(initialState);
  }, []);
  
  // Computed values
  const selectedPageCount = useMemo(() => {
    return state.files.reduce((count, file) => 
      count + file.pages.filter(p => p.selected).length, 0
    );
  }, [state.files]);
  
  const totalPageCount = useMemo(() => {
    return state.files.reduce((count, file) => count + file.pages.length, 0);
  }, [state.files]);
  
  const estimatedTotalTokens = useMemo(() => {
    return state.files.reduce((tokens, file) => 
      tokens + file.pages
        .filter(p => p.selected)
        .reduce((sum, p) => sum + Math.ceil(p.text.length / 4), 0)
    , 0);
  }, [state.files]);
  
  const canProceedFromUpload = useMemo(() => {
    return state.files.length > 0;
  }, [state.files]);
  
  const canProceedFromPreview = useMemo(() => {
    return selectedPageCount > 0;
  }, [selectedPageCount]);
  
  const generationProgress = useMemo(() => {
    if (state.chunks.length === 0) return 0;
    return Math.round((state.currentChunkIndex / state.chunks.length) * 100);
  }, [state.currentChunkIndex, state.chunks.length]);
  
  return {
    // State
    ...state,
    
    // Navigation
    goToStep,
    nextStep,
    prevStep,
    
    // File management
    setFiles,
    addFile,
    addFiles,
    removeFile,
    
    // Page selection
    togglePageSelection,
    selectAllPages,
    selectPageRange,
    updatePageText,
    
    // Chunking
    setChunkingStrategy,
    setBlockSize,
    generateChunks,
    
    // Generation
    startGeneration,
    completeChunk,
    setIsGenerating,
    setPrompt,
    failChunk,
    finishGeneration,
    setGenerationError,
    
    // Reset
    reset,
    
    // Computed
    selectedPageCount,
    totalPageCount,
    estimatedTotalTokens,
    canProceedFromUpload,
    canProceedFromPreview,
    generationProgress
  };
}

export default useWizardState;
